import { random, debounce } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import bacteriasPlug from './assets/bacterias.svg'
import { Wrapper, Inner, Plug, Unit } from './styles'

const COUNT = 19
const MIX_UNIT_S = 40
const MAX_UNIT_S = 70
const TIME = 6000
const friction = 0.01

export default class BacteriasEffect extends Component {
  static propTypes = {
    isBrowser: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  }

  constructor(props) {
    super(props)

    this.intervalId = null
    this.requestAnimationId = null
    this.wrapperRef = React.createRef()
    this.debouncedResize = debounce(this.handleResize, 200)

    // flags
    this.animated = null

    // create array of initial dna nodes position
    this.unitSizes = []
    this.nextUnitsProps = []

    this.state = {
      width: 0,
      height: 0,
      opts: [],
      dontAnimate: true,
      prevIsBrowser: null,
      prevRestrictUIX: null,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { isBrowser, restrictUIX } = nextProps
    const { prevIsBrowser, prevRestrictUIX } = prevState

    if (isBrowser !== prevIsBrowser || restrictUIX !== prevRestrictUIX) {
      return {
        prevIsBrowser: isBrowser,
        prevRestrictUIX: restrictUIX,
        dontAnimate: !!isBrowser.safari || restrictUIX.noAnimation,
      }
    }

    return null
  }

  componentDidMount() {
    this.initAnimation()
    window.addEventListener('resize', this.debouncedResize)
  }

  componentDidUpdate(prevProps, prevState) {
    const { dontAnimate } = this.state

    if (dontAnimate && dontAnimate !== prevState.dontAnimate) {
      this.launch()
    }
  }

  componentWillUnmount() {
    this.destructAnimation()
    window.removeEventListener('resize', this.debouncedResize)
  }

  launch = () => {
    const { dontAnimate } = this.state

    if (!dontAnimate) {
      this.animated = true
      this.animate(true)
    }
  }

  destructAnimation = () => {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    if (this.requestAnimationId) {
      cancelAnimationFrame(this.requestAnimationId)
      this.requestAnimationId = null
    }
  }

  initAnimation = () => {
    const box = this.wrapperRef.current.getBoundingClientRect()
    const opts = []

    for (let i = 0; i < COUNT; i++) {
      opts.push(this.randomProps(i, { width: box.width, height: box.height }))
      this.nextUnitsProps[i] = { ...opts[i] }
    }

    this.setState(
      {
        width: box.width,
        height: box.height,
        opts,
      },
      () => {
        this.launch()
      },
    )
  }

  handleResize = () => {
    const box = this.wrapperRef.current.getBoundingClientRect()

    this.setState({
      width: box.width,
      height: box.height,
    })
  }

  randomProps = (i, sizes = null) => {
    const { width, height, opts } = sizes || this.state

    this.unitSizes[i] = this.unitSizes[i] || random(MIX_UNIT_S, MAX_UNIT_S)
    const prev = opts && opts[i] ? opts[i] : null

    const size = this.unitSizes[i]
    const posX = random(0, width - size)
    const posY = random(0, height - size)
    const dX = prev ? ((posX - prev.x) * (1 - friction) * 10) / TIME : 0
    const dY = prev ? ((posY - prev.y) * (1 - friction) * 10) / TIME : 0

    return {
      x: posX,
      y: posY,
      dX: 0,
      dY: 0,
      fricDX: Math.abs(dX * friction),
      fricDY: Math.abs(dY * friction),
      limitDX: dX,
      limitDY: dY,
    }
  }

  animate = (flagFirst = false) => {
    if (!this.animated) {
      return null
    }

    const { opts } = this.state
    const optsClone = []

    if (flagFirst) {
      this.changeDirection()
      this.intervalId = setInterval(() => this.changeDirection(), TIME)
    }

    for (let i = 0; i < COUNT; i++) {
      // Slowly decrease speed for old direction
      optsClone[i] = { ...opts[i] }
      optsClone[i].dX -= optsClone[i].dX > 0 ? optsClone[i].fricDX : 0
      optsClone[i].dY -= optsClone[i].dY > 0 ? optsClone[i].fricDY : 0
      // Slowly increase speed for new direction
      this.nextUnitsProps[i].dX +=
        Math.abs(this.nextUnitsProps[i].dX) <
        Math.abs(this.nextUnitsProps[i].limitDX)
          ? this.nextUnitsProps[i].limitDX * friction
          : 0
      this.nextUnitsProps[i].dY +=
        Math.abs(this.nextUnitsProps[i].dY) <
        Math.abs(this.nextUnitsProps[i].limitDY)
          ? this.nextUnitsProps[i].limitDY * friction
          : 0
      // Calculate summary speed
      const sumDX = this.nextUnitsProps[i].dX + optsClone[i].dX
      const sumDY = this.nextUnitsProps[i].dY + optsClone[i].dY
      // Calculate next position

      optsClone[i].x += sumDX
      optsClone[i].y += sumDY
    }

    this.setState(
      {
        opts: optsClone,
      },
      () => {
        if (this.requestAnimationId) {
          cancelAnimationFrame(this.requestAnimationId)
        }

        this.requestAnimationId = requestAnimationFrame(() => this.animate())
      },
    )

    return null
  }

  changeDirection = () => {
    const { opts } = this.state
    const optsClone = []

    for (let i = 0; i < COUNT; i++) {
      const { x, y } = opts[i]

      optsClone[i] = this.nextUnitsProps[i]
      optsClone[i].x = x
      optsClone[i].y = y
      this.nextUnitsProps[i] = this.randomProps(i)
    }

    this.setState({
      opts: optsClone,
    })
  }

  renderBacterias = () => {
    const { opts } = this.state

    return opts.map((currentProps, i) => {
      const unit = currentProps
      const size = this.unitSizes[i]

      return (
        <Unit
          key={i}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            transform: `translate(${unit.x}px,${unit.y}px)`,
          }}
        />
      )
    })
  }

  render() {
    const { dontAnimate } = this.state

    return (
      <Wrapper>
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
          <defs>
            <filter id="bacterias_filter">
              <feGaussianBlur
                in="SourceGraphic"
                stdDeviation="10"
                result="blur"
              />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
                result="bacterias_filter"
              />
              <feBlend in="SourceGraphic" in2="bacterias_filter" />
            </filter>
          </defs>
        </svg>
        <Inner ref={this.wrapperRef}>
          {dontAnimate ? <Plug src={bacteriasPlug} /> : this.renderBacterias()}
        </Inner>
      </Wrapper>
    )
  }
}
